import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/** One process execution as the v3 API lists it. */
export interface ExecutionV3 {
    [key: string]: any;

    id: string;
    frontdoorId?: string;
    /** The operation, as a kind: "Create TcpShare". */
    name?: string;
    /** What that operation did, which is the human-readable line: "Create my-database". */
    description?: string;
    status?: ExecutionStatusV3;
    /** The authorization code of what was acted on — `share`, `agent`, `frontdoor`. */
    resourceType?: string;
    resourceId?: string;
    /** Ties every log line of this execution together. Support follows it into Kibana. */
    traceId?: string;
    /** The process behind the execution. A mutation that ran as a plain transaction has none. */
    processId?: string;
    initiatingIdentityId?: string;
    startedAt?: string;
    finishedAt?: string;
    createdAt?: string;
    updatedAt?: string;
    /** Self, the owning frontdoor, and — only while it is running — `cancel`. */
    _links?: Record<string, { href: string }>;
}

/** Where an operation got to. `WARNING` is defined but nothing produces it yet. */
export type ExecutionStatusV3 = 'RUNNING' | 'FAILED' | 'SUCCESS' | 'WARNING' | 'CANCELED';

/**
 * One step of an execution. Which child field is filled says how they ran: `subprocess` nested,
 * `parallelSteps` together, `sequentialSteps` in turn.
 */
export interface ExecutionStepV3 {
    [key: string]: any;

    id: string;
    description?: string;
    stepName?: string;
    status?: string;
    started?: string;
    finished?: string;
    retryCount?: number;
    /** Pre-check output. Present only on steps that check something before they act. */
    data?: any;
    steps?: ExecutionStepV3[];
    subprocess?: { steps?: ExecutionStepV3[] };
    parallelSteps?: ExecutionStepV3[];
    sequentialSteps?: ExecutionStepV3[];
}

/** One page of executions, plus the link that gets the next one. */
export interface ExecutionPageV3 {
    executions: ExecutionV3[];
    /** The service's own link to the next page, or '' at the end. Opaque — nothing here parses it. */
    nextHref: string;
    /** Total across all pages when the service reports one, otherwise null. */
    total: number | null;
}

/**
 * What to narrow the listing by, as `FindExecution.BeanBuilder` binds it. List-valued fields go
 * as repeated parameters, read as "any of these".
 */
export interface ExecutionFilterV3 {
    /**
     * Contains-match on the operation kind, "Create TcpShare". Pass the literal typed;
     * {@link likeContains} adds the wildcards. One value only — `Find.likeOrIn` switches to an
     * exact-match `IN` at two or more.
     */
    name?: string;

    /** Contains-match on what the operation did, "Create my-database". One value, as {@link name}. */
    description?: string;
    statuses?: string[];
    resourceTypes?: string[];
    /**
     * The window the execution started in, as ISO instants, both ends optional. Bounded on
     * `startedAt` because `finishedAt` is null while a run is going.
     */
    startedFrom?: string;
    startedTo?: string;
}

export const DEFAULT_EXECUTION_PAGE_SIZE = 30;

/**
 * Whether a status means the work is still going. The one definition the listing and the navigator
 * both read, so a row that spins can never sit under an indicator that says nothing is happening.
 */
export function isExecutionInFlight(status?: string): boolean {
    return status === 'RUNNING' || status === 'STARTED' || status === 'NEW';
}

/**
 * A literal as a `LIKE` contains-pattern, `%literal%`, with the SQL wildcards inside it escaped.
 * The client's job: `FindExecution` binds text columns through `Find.likeOrIn`, which passes the
 * value to `Operators.like` verbatim and adds no wildcards of its own.
 */
export function likeContains(literal: string): string {
    const escaped = literal.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
    return `%${escaped}%`;
}

/**
 * A time window in the platform's range syntax: `[from--to)` for two ends, `>=from` or `<to` for
 * one, '' for none. Half-open at the top, so a row falls in one of two adjacent windows.
 */
export function instantRange(from?: string, to?: string): string {
    if (from && to) {
        return `[${from}--${to})`;
    }
    if (from) {
        return `>=${from}`;
    }
    if (to) {
        return `<${to}`;
    }
    return '';
}

/**
 * Reads process executions from the v3 frontdoor API, one frontdoor at a time — every path is
 * scoped by its id. Steps are a second request per execution, because a page of them is
 * thousands of steps and almost none are opened.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3ExecutionService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /** The first page of `frontdoorId`'s executions, newest first unless `sort` says otherwise. */
    public findExecutions(
        frontdoorId: string,
        sort = 'startedAt,DESC',
        filter: ExecutionFilterV3 = {},
        size = DEFAULT_EXECUTION_PAGE_SIZE,
        page = 0
    ): Observable<ExecutionPageV3> {
        let params = new HttpParams().set('sort', sort).set('size', String(size)).set('page', String(page));
        if (filter.name) {
            params = params.set('name', likeContains(filter.name));
        }
        if (filter.description) {
            params = params.set('description', likeContains(filter.description));
        }
        // Appended, not set, so several values stay several parameters.
        (filter.statuses ?? []).forEach((status) => {
            params = params.append('status', status);
        });
        (filter.resourceTypes ?? []).forEach((resourceType) => {
            params = params.append('resourceType', resourceType);
        });
        const startedAt = instantRange(filter.startedFrom, filter.startedTo);
        if (startedAt) {
            params = params.set('startedAt', startedAt);
        }
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/executions`, { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    /** The next page. The link is followed verbatim — its cursor only resumes under the order that made it. */
    public followNext(nextHref: string): Observable<ExecutionPageV3> {
        return this.http
            .get(nextHref, { headers: this.headers() })
            .pipe(map((response: any) => this.readPage(response)));
    }

    /** The step tree of one execution. It rides on the execution under `_meta`, so this re-reads the execution. */
    public getProcessData(frontdoorId: string, executionId: string): Observable<ExecutionStepV3 | null> {
        const params = new HttpParams().set('meta', 'processData');
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/executions/${executionId}`, { headers: this.headers(), params })
            .pipe(map((response: any) => response?._meta?.processData ?? response?.processData ?? null));
    }

    /**
     * Stops a running execution by following the `cancel` link the service put on it, so nothing
     * here builds the path or reads the status to decide. The process runs its steps'
     * compensating actions. Answers with the execution, now `CANCELED`.
     */
    public cancelExecution(cancelHref: string): Observable<ExecutionV3> {
        return this.http
            .post(cancelHref, {}, { headers: this.headers() })
            .pipe(map((response: any) => response as ExecutionV3));
    }

    /* Internals -------------------------------------------------------------- */

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    /** Accepts the HAL collection, a bare array, or a Spring page, so the shape can settle later. */
    private readPage(response: any): ExecutionPageV3 {
        if (Array.isArray(response)) {
            return { executions: response, nextHref: '', total: response.length };
        }
        const executions = this.readList(response);
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : null;
        return { executions, nextHref: response?._links?.next?.href ?? '', total };
    }

    private readList(response: any): ExecutionV3[] {
        const embedded = response?._embedded;
        if (embedded) {
            const collection = embedded.executions ?? embedded.executionList ?? Object.values(embedded)[0];
            if (Array.isArray(collection)) {
                return collection;
            }
        }
        if (Array.isArray(response?.content)) {
            return response.content;
        }
        this.logger.error('findExecutions: unrecognised response shape', response);
        return [];
    }
}
